import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp, router } from "@inertiajs/react"
import { StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

const appName = import.meta.env.VITE_APP_NAME ?? "Go Between"

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  // Lazy glob (no `eager`): each page compiles to its own chunk and is fetched
  // on demand. Without this every page — including the heavy
  // working-with-robots suite and the full motion library — shipped in one
  // ~1.9MB blob parsed on every visit, which is what made the home page feel
  // slow after the hero animation grew the shared bundle. The current page's
  // chunk is modulepreloaded from the Vite manifest, so the initial load still
  // resolves without an extra round-trip; only navigations to other routes pay
  // a (cached-after-first) fetch. SSR (ssr.ts) stays eager — it resolves
  // server-side and bundles separately.
  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>([
      "../pages/**/*.tsx",
      "!../pages/**/*.test.tsx",
    ])
    const importPage = pages[`../pages/${name}.tsx`]
    if (!importPage) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    return importPage().then((page) => {
      // To use a default layout, import the Layout component
      // and use the following line.
      // see https://inertia-rails.dev/guide/pages#default-layouts
      //
      page.default.layout ??= [PersistentLayout]
      return page
    })
  },

  setup({ el, App, props }) {
    if (el.hasChildNodes()) {
      hydrateRoot(
        el,
        <StrictMode>
          <App {...props} />
        </StrictMode>,
      )
      return
    }
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()

// A back/forward-cache restore can resurrect a snapshot taken before the
// current deploy, leaving the old bundle on screen until the next navigation.
// Reloading sends the snapshot's asset version, so a stale one gets Inertia's
// 409 → full-page visit onto the current build; a matching one just refreshes
// props.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    router.reload()
  }
})
